import { DataKey } from '../../lib/model';
import ControlPanelLayout, {
	IControlPanelLayoutProps
} from '../Controls/ControlPanelLayout';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Typography,
	useTheme
} from '@mui/joy';
import { useEdgeCases } from '../../providers/EdgeCaseProvider';
import { STATES_WITH_EDGE_CASES } from '../../lib/const';
import { useRoute } from '../../providers/RouteProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useChartSectionData } from '../../providers/ChartSectionProvider';
import BarChart from './BarChart';
import DisaggChart from './DisaggChart';

interface IProps extends IControlPanelLayoutProps {
	accordionIndex: number | null;
	setAccordionIndex: (arg0: number | null) => void;
	print?: boolean;
}
const TotalRaisedLayout = ({
	page,
	title,
	text,
	accordionIndex,
	setAccordionIndex,
	print = false
}: IProps) => {
	const theme = useTheme();
	const { selectedState, election } = useRoute();
	const { listOfOffices } = useEdgeCases();
	const { chartData } = useChartSectionData();

	const hasGroupsThatNeedLegend = () => {
		const groups = chartData
			?.map((array) => {
				return array.map((item) => item.group);
			})
			.flat();
		const xx = groups
			.map((grp) => {
				if (grp.length) {
					if (
						grp.includes('MENA') ||
						grp.includes('NatAm') ||
						grp.includes('AAPI')
					)
						return true;
				} else return null;
			})
			.filter((item) => item);
		return xx.length ? true : false;
	};
	const showLegend = hasGroupsThatNeedLegend();
	//console.log('show', showLegend);
	const topic = DataKey.TotalRaised;

	const hasEdgeCases =
		STATES_WITH_EDGE_CASES.includes(selectedState as string) &&
		page === 'state';

	const matchesLG = useMediaQuery(theme.breakpoints.up(1140));
	const matchesMD = useMediaQuery(theme.breakpoints.up(850));

	const gridLayout = () => {
		if (hasEdgeCases && election === 'state') {
			if (matchesLG) return '1fr 1fr 1fr';
			else if (matchesMD) return '1fr 1fr';
			else return '1fr';
		} else if (matchesMD) return '1fr 1fr';
		else return '1fr';
	};
	const getFirstRowCount = () => {
		if (hasEdgeCases && election === 'state') {
			if (matchesLG) return 3;
			else if (matchesMD) return 2;
			else return 1;
		} else if (matchesMD) return 2;
		else return 1;
	};
	//console.log('chartdata in tr', chartData);
	const extendedList = listOfOffices?.concat(['State Senate', 'State House']);
	return (
		<>
			<ControlPanelLayout page={page} title={title} text={text} print={print} />
			<Box
				sx={{
					backgroundColor: theme.palette.chartElementsBackground.bg,
					height: '100%',
					padding: '1rem 3rem'
				}}
			>
				<Box sx={{ mx: { xs: '-1rem', md: '-3rem' } }}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: gridLayout,
							flexGrow: 1
						}}
					>
						{/* edgecases */}
						{page === 'state' && election === 'state' && hasEdgeCases ? (
							<>
								{extendedList?.map((office, index) => {
									return index < getFirstRowCount() ? (
										<BarChart
											key={index}
											office={office}
											chartData={chartData[index]}
											topic={topic} //for tooltip
											print={print}
										/>
									) : null;
								})}
							</>
						) : (
							<>
								{/* //when no edge cases */}
								<BarChart
									office={'State Senate'}
									topic={topic}
									page={page}
									chartData={chartData[0]}
									print={print}
								/>

								{page === 'state' && election === 'state' ? (
									<BarChart
										office={'State House'}
										topic={topic}
										page={page}
										chartData={chartData[1]}
										print={print}
									/>
								) : (
									<DisaggChart
										type={'money'}
										topic={topic}
										page={page}
										chartData={chartData[1]}
										print={print}
									></DisaggChart>
								)}
							</>
						)}
					</Box>
				</Box>
				{page === 'state' &&
				election === 'state' &&
				hasEdgeCases &&
				(extendedList as string[]).length > getFirstRowCount() ? (
					<Accordion
						expanded={accordionIndex === 0}
						onChange={(_event, expanded) => {
							setAccordionIndex(expanded ? 0 : null);
						}}
						sx={{
							boxShadow: 'none',
							position: 'unset',
							marginTop: '1rem',
							marginLeft: '-1.75rem'
						}}
					>
						<AccordionSummary
							sx={{
								padding: '0 0 0 1.6rem',
								justifyContent: 'flex-start',
								'> button': { justifyContent: 'unset' }
							}}
						>
							{accordionIndex === 0 ? 'See Less' : 'See More'}
						</AccordionSummary>
						<AccordionDetails
							sx={{
								//padding: '2rem 0rem',
								flexGrow: 1
							}}
						>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: gridLayout,
									flexGrow: 1,
									paddingLeft: '1.6rem',
									mx: { xs: '-1rem', md: '-3rem' }
								}}
							>
								{/* //rest of edgecases */}
								{election === 'state' && hasEdgeCases ? (
									<>
										{extendedList?.map((office, index) => {
											return index >= getFirstRowCount() ? (
												<BarChart
													key={index}
													office={office}
													chartData={chartData[index]}
													topic={topic}
													print={print}
												/>
											) : null;
										})}
									</>
								) : null}
							</Box>
						</AccordionDetails>
					</Accordion>
				) : null}
			</Box>
			{showLegend ? (
				<Typography
					sx={{
						padding: '0 0 1rem 3rem',
						backgroundColor: theme.palette.chartElementsBackground.bg
					}}
				>
					MENA - Middle Eastern/North African &nbsp; NatAm - Native
					American/Alaska Native/Native Hawaiian &nbsp; AAPI - Asian
					American/Pacific Islander
				</Typography>
			) : null}
		</>
	);
};

export default TotalRaisedLayout;
